import React, { useState, useEffect } from 'react';
import { X, MessageCircle } from 'lucide-react';

const PHONE_E164 = '447916620316';
const PHONE_DISPLAY = '+44 7916 620316';
const DEFAULT_MSG = encodeURIComponent("Hi FX Astra team — I have a question about your platform.");
const STORAGE_KEY = 'fx_whatsapp_dismissed_at';
// Re-show the bubble peek 24h after dismissal — the badge itself stays.
const RESHOW_AFTER_MS = 24 * 60 * 60 * 1000;

/**
 * Floating WhatsApp live-chat badge — visible site-wide.
 *   • Pulsing brand badge (bottom-right) opens wa.me deep-link.
 *   • Small "peek" bubble appears 4s after page load, dismissible.
 *   • Peek auto-recurrs every 24h based on localStorage timestamp.
 */
export default function WhatsAppChatBadge() {
  const [peekOpen, setPeekOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const dismissedAt = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10) || 0;
    const eligible = Date.now() - dismissedAt > RESHOW_AFTER_MS;
    if (!eligible) return undefined;
    const t = setTimeout(() => {
      if (!cancelled) setPeekOpen(true);
    }, 4000);
    return () => { cancelled = true; clearTimeout(t); };
  }, []);

  const dismissPeek = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setPeekOpen(false);
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  };

  const waHref = `https://wa.me/${PHONE_E164}?text=${DEFAULT_MSG}`;

  return (
    <div className="fixed bottom-5 right-5 z-[60] pointer-events-none">
      {/* Peek card */}
      {peekOpen && (
        <div
          data-testid="whatsapp-peek"
          className="pointer-events-auto mb-3 max-w-[260px] rounded-md border border-black/10 overflow-hidden animate-in slide-in-from-bottom-2 fade-in" style={{ backgroundColor: '#ffffff' }}
          style={{ boxShadow: '0 12px 28px rgba(0,0,0,0.25)' }}
        >
          <div className="flex items-center justify-between gap-2 px-3 py-2 text-white" style={{ backgroundColor: '#15151c' }}>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-white/15 flex items-center justify-center">
                <MessageCircle className="h-3.5 w-3.5" />
              </div>
              <div className="leading-tight">
                <p className="text-[12px] font-bold">FX Astra Support</p>
                <p className="text-[10px] opacity-80">Typically replies within minutes</p>
              </div>
            </div>
            <button
              onClick={dismissPeek}
              aria-label="Dismiss"
              className="p-1 rounded hover:bg-white/10 transition"
              data-testid="whatsapp-peek-dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="p-3 text-[12px] text-gray-700">
            <p className="bg-gray-100 rounded-2xl rounded-tl-sm px-3 py-2 leading-relaxed">
              👋 Need help? Chat with us on WhatsApp — <span className="font-mono whitespace-nowrap">{PHONE_DISPLAY}</span>
            </p>
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-peek-cta"
              className="mt-3 w-full inline-flex items-center justify-center gap-1.5 bg-primary hover:bg-primary/90 text-white font-semibold text-[12px] py-2 rounded-lg transition"
              onClick={() => { localStorage.setItem(STORAGE_KEY, String(Date.now())); }}
            >
              <MessageCircle className="h-3.5 w-3.5" />
              Start chat
            </a>
          </div>
        </div>
      )}

      {/* Floating badge */}
      <a
        href={waHref}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-badge"
        aria-label={`Chat on WhatsApp ${PHONE_DISPLAY}`}
        className="pointer-events-auto relative inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary text-white hover:scale-105 active:scale-95 transition-transform"
        style={{ boxShadow: '0 8px 22px rgba(37,211,102,0.45)' }}
      >
        {/* Pulse ring */}
        <span className="absolute inset-0 rounded-full bg-primary opacity-70 animate-ping" aria-hidden />
        {/* Official WhatsApp glyph */}
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" className="relative w-7 h-7" fill="currentColor" aria-hidden>
          <path d="M19.11 17.61c-.31-.16-1.83-.9-2.11-1-.28-.1-.48-.16-.69.16s-.79 1-.97 1.2c-.18.2-.36.22-.66.07-.31-.15-1.31-.48-2.5-1.54-.92-.82-1.55-1.84-1.74-2.15-.18-.31-.02-.48.14-.63.14-.14.31-.36.47-.54.16-.18.21-.3.31-.5.1-.2.05-.38-.02-.54-.07-.16-.69-1.66-.95-2.27-.25-.6-.5-.52-.69-.53l-.59-.01c-.2 0-.54.07-.82.38-.28.31-1.08 1.06-1.08 2.59 0 1.53 1.11 3 1.26 3.2.16.2 2.18 3.35 5.3 4.7.74.32 1.31.51 1.76.65.74.24 1.41.2 1.94.12.59-.09 1.83-.75 2.09-1.47.26-.72.26-1.34.18-1.47-.08-.13-.28-.2-.59-.36z M16.02 4C9.41 4 4.04 9.37 4.04 16c0 2.11.55 4.16 1.6 5.97L4 28l6.18-1.61c1.74.95 3.7 1.45 5.68 1.45h.01c6.61 0 11.98-5.37 11.98-12S22.63 4 16.02 4zm0 21.86h-.01c-1.77 0-3.5-.48-5.01-1.37l-.36-.21-3.67.96.98-3.58-.23-.37C6.84 19.94 6.3 18 6.3 16.01c0-5.36 4.37-9.72 9.72-9.72 2.6 0 5.04 1.01 6.87 2.85 1.84 1.84 2.85 4.28 2.85 6.88 0 5.36-4.37 9.84-9.72 9.84z"/>
        </svg>
      </a>
    </div>
  );
}
